import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterOutlet } from '@angular/router';
import { AccessRequestEvent } from '@app/+state/access-requests';
import { HubConnectionState } from '@app/+state/signalR/hub-connection-state';
import { UiStateFacade } from '@app/+state/ui-state/store';
import { TENANT_MGMT_ROUTES } from '@app/+state/user-data/user.model';
import { Configuration } from '@app/models/config.model';
import { CommonConstants } from '@core/common-constants';
import { concatLatestFrom } from '@ngrx/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AppFlow,
  BannerType,
  ControlPageFacade,
  FeatureFlagsFacade,
  FlagValue,
  GainsightFacade,
  ITenantDetail,
  SelectedTenant,
  SignalRFacade,
  TenantContext,
  UserDataFacade,
  UserFeedback,
} from '@ra-state';
import { IInnerButtonClickedEvent, NotificationType } from '@ra-web-tech-ui-toolkit/components';
import {
  Banner,
  CustomAction,
  CustomActionPredicate,
  LanguageItem,
  NavBarTheme,
  NavItem,
  NavItemActions,
  UserNotification,
} from '@rockwell-automation-inc/common-utils';
import { LayoutModule } from '@rockwell-automation-inc/layout';
import { CommonService, ConfigService, HubConnectionError, SignalrService } from '@rockwell-automation-inc/service';
import { LoggerService } from '@servicesV2/logger.service';
import { PermissionsService } from '@servicesV2/permissions.service';
import { DateTime } from 'luxon';
import * as R from 'remeda';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  from,
  fromEvent,
  iif,
  map,
  mergeMap,
  of,
  scan,
  switchMap,
  take,
  takeWhile,
  tap,
  timer,
  toArray,
  withLatestFrom,
} from 'rxjs';
import { AuthenticationService } from '../auth/auth.service';
import { FeedbackComponent } from '../shared/feedback-modal/feedback.component';
import { getAppFlow } from '../shared/utils';
import { SignalrStatusComponent } from '../standalone/signalr-status/signalr-status.component';
import {
  HelpLinksConfig,
  OrganizationLinksConfig,
  ShellNavigationConstants,
  UserSectionLinksConfig,
} from './nav.config';
import { NavigationComponent } from './navigation/navigation.component';
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-shell',
  styleUrls: ['./shell.component.scss'],
  templateUrl: './shell.component.html',
  imports: [LayoutModule, NavigationComponent, SignalrStatusComponent, RouterOutlet, AsyncPipe, TranslateModule],
})
export class ShellComponent {
  authService = inject(AuthenticationService);
  translate = inject(TranslateService);
  private readonly signalRService = inject(SignalrService);
  private readonly commonService = inject(CommonService);
  private readonly dialog = inject(MatDialog);
  private readonly controlPageFacade = inject(ControlPageFacade);
  private readonly userDataFacade = inject(UserDataFacade);
  private readonly logger = inject(LoggerService);
  private readonly signalRFacade = inject(SignalRFacade);
  private readonly router = inject(Router);
  private readonly gainsightFacade = inject(GainsightFacade);
  private readonly featureFlagsFacade = inject(FeatureFlagsFacade);
  private readonly configService = inject<ConfigService<Configuration>>(ConfigService);
  private readonly datePipe = inject(DatePipe);
  private readonly uiStateFacade = inject(UiStateFacade);
  private readonly permissionsService = inject(PermissionsService);

  private readonly MAINTENANCE_WINDOW_PRE_WARNING_DAYS = 5;
  navbarTheme = NavBarTheme.DARK;
  OPENICON = 'ra-icon-ide-md-open';

  shouldShowBreadcrumb: boolean = false;
  private readonly DASHBOARD_PATH = '/dashboard';
  private readonly SUB_ORGS_PATH = '/suborgs';
  private readonly REDEEM_PATH = '/redeem';
  private readonly BUILD_INFO_PATH = '/about/buildInfo';
  timeoutBannerMessage: string;
  bannerItems: Banner[] = [];
  notificationAction$: Observable<any>;

  selectedTenant: SelectedTenant;
  userID$ = this.userDataFacade.userID$;
  selectedTenant$ = this.userDataFacade.selectedTenant$.pipe(
    tap((selectedTenant) => {
      this.selectedTenant = selectedTenant;
    }),
  );
  userName$ = this.userDataFacade.userName$;
  currentCustomProvisioningData$ = this.userDataFacade.currentCustomProvisioningData$;
  showNoLastAccessedTenantModal$ = this.userDataFacade.noLastAccessedTenant$.pipe(
    concatLatestFrom(() => [this.routeSnapshot$]),
    map(
      ([noLastAccessedTenant, routeSnapshot]) =>
        noLastAccessedTenant && getAppFlow(routeSnapshot.url) !== AppFlow.JoinOrgRequest,
    ),
    distinctUntilChanged(),
  );

  queryParams$ = this.controlPageFacade.routeState$.pipe(
    filter((routeState) => routeState?.flow === AppFlow.Invitation),
    tap((routeState) => {
      const routeSnapshot: any = routeState?.snapshot;
      if (routeSnapshot) {
        const invitationId = routeSnapshot.queryParams.invitationId;
        this.userDataFacade.invitations.acceptInvitation(invitationId);
      }
    }),
  );

  // Session Timeout
  countDownTimer$: Observable<number>;
  countDownTime: number;

  updateOrganizations$ = this.commonService.updateOrganizations$;

  signalRConnected$ = this.signalRFacade.isHubConnected$;

  private readonly retryButtonDisabled$ = new BehaviorSubject<boolean>(false);
  private signalRBannerMessage =
    'We are having some internal issues. The portal is currently in read-only mode. Click "retry" to reestablish the connection';

  signalRConnectionState$ = combineLatest([this.signalRFacade.connectionState$, this.signalRFacade.lastError$]).pipe(
    tap(([state, lastError]) => {
      this.logger.log('SignalR Connection State: ', state);
      if (state === HubConnectionState.Reconnecting) {
        this.signalRBannerMessage =
          'We are having some internal issues. The portal is currently in read-only mode. We are trying to re-establish the connection';
        this.showSignalRDisconnectedBanner(this.signalRBannerMessage);
        this.retryButtonDisabled$.next(true);
      } else if (state === HubConnectionState.Disconnected) {
        if (lastError instanceof HubConnectionError && lastError.isNonRecoverable) {
          this.signalRBannerMessage =
            'We could not reestablish the connection. The portal is currently in read-only mode. Try logging-in again.';
          this.retryButtonDisabled$.next(true);
        } else {
          this.signalRBannerMessage =
            'We are having some internal issues. The portal is currently in read-only mode. Click "retry" to reestablish the connection';
          this.retryButtonDisabled$.next(false);
        }
        this.showSignalRDisconnectedBanner(this.signalRBannerMessage);
      } else if (state === HubConnectionState.Connected) {
        this.closeBanner(BannerType.SignalRDisconnected);
      }
    }),
  );

  helpItems: NavItem[];
  navItems: NavItem[];
  private navItemsConfig: NavItem[];
  organizationLinks: NavItem[];
  ORGANIZATION_LINKS: NavItem[] = [];

  enableThemeSwitcher$: Observable<any> = this.featureFlagsFacade.getFlagValue$('enable_theme_switcher');
  enableUserAssignedTrials$: Observable<FlagValue> =
    this.featureFlagsFacade.getFlagValue$('enable_user_assigned_trials');

  techSupportURL$ = this.userDataFacade.userData$.pipe(
    withLatestFrom(this.featureFlagsFacade.getFlagValue$('enable_tech_support')),
    filter(([_userData, _enableTechSupport]) => Boolean(_enableTechSupport)),
    map(([_userData, _enableTechSupport]) => {
      const userEmail = _userData.email;
      return (
        CommonConstants.CONTACT_SUPPORT_URL +
        encodeURIComponent(userEmail) +
        this.configService.config.techSupportUrlPath
      );
    }),
  );

  helpMenuToOpen$ = this.controlPageFacade.helpMenuToOpen$.pipe(
    filter((navItem) => Boolean(navItem?.id)),
    map((navItem) => navItem),
  );

  supportedLanguages = this.configService.config.supportedLanguages || [];
  selectedLang: LanguageItem = this.supportedLanguages[0];

  routeSnapshot$ = this.controlPageFacade.routeStoreState$.pipe(
    tap((routeState) => {
      this.shouldShowBreadcrumb = !routeState.url?.includes(this.REDEEM_PATH);
    }),
  );

  displayLanguageToggle$ = this.featureFlagsFacade.getFlagValue$('enable_language_switcher').pipe(
    map((languageSwitcherEnabled) => {
      return Boolean(languageSwitcherEnabled);
    }),
  );

  languageChanged$ = this.translate.onLangChange.pipe(
    tap(() => {
      this.doTranslate(this.navItemsConfig);
      this.doTranslate(HelpLinksConfig.BASE_LINKS);
    }),
  );

  generateNavigationLinks$ = combineLatest([
    this.userDataFacade.currentTenantContext$,
    this.userDataFacade.userName$,
    this.userDataFacade.preferredLanguage$,
    this.enableUserAssignedTrials$,
  ]).pipe(
    switchMap(([tenantContext, userName, preferredLanguage, enableUserTrials]) => {
      const selectedLang = this.supportedLanguages.filter((item) => {
        return item.langCode === preferredLanguage;
      })[0];
      this.selectedLang = selectedLang;
      return iif(
        () => tenantContext === 'NotSelected' || tenantContext === 'Personal',
        combineLatest([
          of(tenantContext),
          of(userName),
          of(null),
          this.translate.use(String(preferredLanguage)),
          of(enableUserTrials),
        ]),
        combineLatest([
          of(tenantContext),
          of(userName),
          this.userDataFacade.currentTenant$,
          this.translate.use(String(preferredLanguage)),
          of(enableUserTrials),
        ]),
      );
    }),
    switchMap(([tenantContext, userName, tenant, _lang, enableUserTrials]) => {
      const userLinks = this.genUserLinks(userName, tenantContext, enableUserTrials);
      if (tenant === null) {
        return combineLatest([of(userLinks), of([] as NavItem[])]);
      }
      return combineLatest([of(userLinks), this.genOrgLinks$(tenantContext, tenant)]);
    }),
    tap(([userLinks, orgLinks]) => {
      this.navItemsConfig = [...userLinks, ...orgLinks];
      if (this.navItemsConfig) {
        this.navItems = this.doTranslate(this.navItemsConfig);
      }
      this.helpItems = this.doTranslate(HelpLinksConfig.BASE_LINKS);
    }),
  );

  private doTranslate(navItems?: NavItem[]): NavItem[] {
    return (
      navItems?.map((link) => ({
        ...link,
        displayName: this.translate.instant(link.displayName),
        children: link.children?.map((child) => ({
          ...child,
          displayName: this.translate.instant(child.displayName),
        })),
      })) ?? []
    );
  }

  getParentOrgName(currentTenant): string {
    if (!currentTenant?.parent) {
      return '';
    }
    const tenant = this.accessibleTenants?.find((tenant) => tenant.id === currentTenant?.parent);
    return tenant ? tenant.name : '';
  }

  translationArr = [{ key: 'homeMenuTitle', text: 'fthub.factorytalk_hub_home' }];

  genUserLinks(userName: string, tenantContext: TenantContext, enableUserAssignedTrials: any): NavItem[] {
    const userSectionLinks: NavItem[] = [
      {
        displayName: userName,
        id: 'user',
        isHeading: true,
        isDisabled: true,
        showUserInitials: true,
        children: [],
      },
    ];

    userSectionLinks[0]?.children?.push(
      UserSectionLinksConfig.ORGANIZATIONS_LINK,
      ...(enableUserAssignedTrials
        ? [
            {
              action: NavItemActions.self,
              displayName: UserSectionLinksConfig.MY_ENTITLEMENTS_LINK.displayName,
              id: UserSectionLinksConfig.MY_ENTITLEMENTS_LINK.id,
              route: UserSectionLinksConfig.MY_ENTITLEMENTS_LINK.route,
            },
          ]
        : []),
    );
    if (tenantContext === 'NotSelected' || tenantContext === 'Personal') {
      userSectionLinks[0]?.children?.push({
        action: NavItemActions.self,
        displayName: UserSectionLinksConfig.ORG_ENTITLEMENTS_LINK.displayName,
        id: UserSectionLinksConfig.ORG_ENTITLEMENTS_LINK.id,
        route: UserSectionLinksConfig.ORG_ENTITLEMENTS_LINK.route,
      });
      return [...userSectionLinks, UserSectionLinksConfig.PLACEHOLDER_ORGANIZATIONS_LINK];
    } else {
      return userSectionLinks;
    }
  }
  /**
   * Filter organization links
   */
  genOrgLinks$(tenantContext: TenantContext, tenant?: ITenantDetail): Observable<NavItem[]> {
    if (tenantContext === 'NotSelected' || tenantContext === 'Personal') {
      return of([]);
    }
    if (!tenant) {
      this.logger.error('Tenant context is set but tenant details are not available');
      return of([]);
    }

    const isSubOrg = Boolean(tenant.parent);
    return from(OrganizationLinksConfig.BASE_LINKS).pipe(
      mergeMap((link) => {
        if (link.item.id === ShellNavigationConstants.SUB_ORGANIZATION_LINK_ID && isSubOrg) {
          return of({ item: link.item, isMet: false });
        }
        const condition = link?.condition;
        if (!condition) {
          return of({ item: link.item, isMet: true });
        }
        const { permissions, operator = 'any' } = condition;
        const hasPermission$ =
          operator === 'all'
            ? this.permissionsService.hasAll$(permissions)
            : this.permissionsService.hasAny$(permissions);

        return hasPermission$.pipe(
          take(1),
          map((hasPermission) => ({ item: link.item, isMet: hasPermission })),
        );
      }),
      filter((result) => result.isMet),
      map((result) => {
        // Refactor: use template to inject tenant id in route instead of string manipulation
        const route = result.item.route?.split('/').pop();
        return { ...result.item, route: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${tenant.id}/${route}` };
      }),
      toArray(),
      map((links) => {
        return [
          {
            children: links,
            displayName: tenantContext.name,
            id: 'tenantName',
            isHeading: true,
            isDisabled: true,
            // Refactor: should this be here?
            headerImage: tenant.logoUrl ?? '',
            parentOrgName: this.getParentOrgName(tenant),
          },
        ];
      }),
    );
  }

  helpItemClicked(item: NavItem): void {
    if (item.id === 'provide-feedback') {
      this.openFeedbackComponent();
    } else if (item.id === 'build-information') {
      this.router.navigate([this.BUILD_INFO_PATH]);
    } else {
      window.open(item.route, item.action);
    }
  }

  sessionTimedOut$ = this.controlPageFacade.sessionTimedOut$.pipe(
    filter((sessionTimedOut) => sessionTimedOut),
    map((sessionTimedOut) => {
      this.showSessionTimeoutBanner();
      this.countDownTimer$ = this.startCountDownTimer$();
      return sessionTimedOut;
    }),
  );
  click$ = fromEvent(document, 'click').pipe(
    concatLatestFrom(() => [this.controlPageFacade.sessionTimedOut$]),
    filter(([, sessionTimedOut]) => sessionTimedOut),
    tap(() => {
      this.controlPageFacade.extendSession();
      this.closeBanner(BannerType.SessionTimeout);
    }),
  );

  closeBanner(bannerType: BannerType): void {
    const banner = this.bannerItems.find((banner) => banner.id === bannerType);
    if (banner) {
      this.hideBanner(banner);
    }
  }

  hideBanner(banner: Banner): void {
    this.bannerItems = this.bannerItems.filter((bannerItem) => bannerItem.id !== banner.id);

    // Re-check SignalR connection state when closing the maintenance banner
    if (banner.id === BannerType.MaintenanceWindow) {
      this.recheckSignalRConnectionState();
    }
  }

  private recheckSignalRConnectionState(): void {
    this.signalRFacade.connectionState$.pipe(take(1)).subscribe((state) => {
      if (state === HubConnectionState.Disconnected) {
        this.showSignalRDisconnectedBanner(this.signalRBannerMessage);
      }
    });
  }

  private showSignalRDisconnectedBanner(msg: string): void {
    const signalRDisconnectedBanner: Banner = {
      show: true,
      message: msg,
      config: {
        showDefaultIcon: false,
        showCloseIcon: false,
        useNotificationBackground: true,
        notificationType: NotificationType.Warning,
      },
      buttons: [
        {
          label: 'Retry',
          icon: 'ra-icon-ide-sm-autobrowse-enabled',
          // eslint-disable-next-line rxjs/finnish
          disabled: this.retryButtonDisabled$.asObservable(),
          data: {
            action: 'retry-signalr-connection',
          },
        },
      ],
      raIcon: 'ra-icon-ide-md-connect',
      id: BannerType.SignalRDisconnected,
    };
    this.closeBanner(BannerType.SignalRDisconnected);
    if (!this.bannerItems.some((x) => x.id === BannerType.MaintenanceWindow)) {
      this.addBannerToTray(signalRDisconnectedBanner);
    }
  }

  setCountDownTime(countDownTime: number): void {
    this.countDownTime = countDownTime;
  }

  startCountDownTimer$(): Observable<number> {
    const countDownTime = this.countDownTime || CommonConstants.sessionTimeout;

    return timer(0, 1000).pipe(
      scan((acc) => acc - 1, countDownTime),
      takeWhile((x) => {
        // REVISIT: This need to be implemented as per admin portal
        // Take while the timer is greater than or equal to 0 and the session timeout banner is shown
        // earlier code did not complete when the banner removed and hence resulted in logout even though user extended session
        const sessionTimeoutBanner = this.bannerItems.find((banner) => banner.id === BannerType.SessionTimeout);
        return x >= 0 && sessionTimeoutBanner?.show === true;
      }),
      tap((timer) => {
        if (timer === 0) {
          this.controlPageFacade.logoutSession();
        }
      }),
      map((seconds) => seconds * 1000),
      tap((timer) => {
        this.timeoutBannerMessage = `Session timeout in ${this.datePipe.transform(
          timer,
          'mm:ss',
          'UTC',
        )}s due to inactivity. Click anywhere to continue your session`;
        const sessionTimeoutBanner = this.bannerItems.find((banner) => banner.id === BannerType.SessionTimeout);
        if (sessionTimeoutBanner) {
          sessionTimeoutBanner.message = this.timeoutBannerMessage;
        }
      }),
    );
  }

  addBannerToTray(banner: Banner): void {
    if (this.bannerItems.findIndex((x) => x.id === banner.id) === -1) {
      this.bannerItems.push(banner);
    }
  }

  showSessionTimeoutBanner(): void {
    const sessionTimeOutBanner: Banner = {
      show: true,
      message: this.timeoutBannerMessage,
      config: {
        showDefaultIcon: true,
        showCloseIcon: false,
        useNotificationBackground: false,
        notificationType: NotificationType.Warning,
      },
      buttons: [
        {
          label: 'Continue',
        },
      ],
      raIcon: '',
      id: BannerType.SessionTimeout,
    };
    this.addBannerToTray(sessionTimeOutBanner);
  }

  openFeedbackComponent(): void {
    this.dialog
      .open(FeedbackComponent, { disableClose: true, width: '560px' })
      .afterClosed()
      .subscribe((feedback: UserFeedback) => {
        if (feedback) {
          this.userDataFacade.shareFeedback(feedback);
        }
      });
  }

  menuItemClicked(item: NavItem): void {
    this.router.navigate([item.route]);
  }

  logoutClicked(): void {
    this.gainsightFacade.resetSession();
    this.authService.logout();
  }

  goToHome(): void {
    this.router.navigate([this.DASHBOARD_PATH]);
  }

  bannerButtonClicked($event: IInnerButtonClickedEvent): void {
    if ($event.button.data) {
      if ($event.button.data.action === 'retry-signalr-connection') {
        this.retryButtonDisabled$.next(true);
        this.signalRFacade.retryHubConnection();
      } else {
        this.router.navigate([$event.button.data.redirectUrl]);
      }
    }
  }

  showMaintenanceWindowBanner(message: string, notificationType: NotificationType, bannerIcon: string): void {
    const maintenanceBanner: Banner = {
      show: true,
      message: message,
      config: {
        showDefaultIcon: true,
        showCloseIcon: true,
        useNotificationBackground: true,
        notificationType: notificationType,
      },
      buttons: [],
      raIcon: bannerIcon,
      id: BannerType.MaintenanceWindow,
    };
    this.closeBanner(BannerType.SignalRDisconnected);
    this.addBannerToTray(maintenanceBanner);
  }

  maintenanceWindow$ = this.featureFlagsFacade.maintenanceWindow$.pipe(
    tap((maintenanceWindow) => {
      const start = maintenanceWindow.from.toLocaleString(DateTime.DATETIME_MED);
      const end = maintenanceWindow.to.toLocaleString(DateTime.DATETIME_MED);
      if (maintenanceWindow.isActive) {
        const msg = `Maintenance in progress: FactoryTalk Hub is going through scheduled maintenance until ${end}. The portal is currently in read-only mode.`;
        this.showMaintenanceWindowBanner(msg, NotificationType.Warning, 'ra-icon-ide-sm-root-asset');
      } else if (maintenanceWindow.startsWithinDays(this.MAINTENANCE_WINDOW_PRE_WARNING_DAYS)) {
        this.showMaintenanceWindowBanner(
          `Upcoming Scheduled Maintenance: ${start} to ${end}. FactoryTalk Hub portal will be available in read-only mode during this time. Access to other services will not be affected.`,
          NotificationType.Info,
          'ra-icon-ide-sm-alarm',
        );
      } else {
        // MW was disabled
        this.closeBanner(BannerType.MaintenanceWindow);
      }
    }),
  );

  maintenanceWindowCompleted$ = this.featureFlagsFacade.maintenanceWindowCompleted$.pipe(
    tap(() => {
      this.logger.log('hiding mw banner');
      this.closeBanner(BannerType.MaintenanceWindow);
    }),
  );

  accessibleTenants;

  accessibleTenants$ = this.userDataFacade.accessibleTenants$.pipe(
    tap((accessibleTenants) => {
      this.accessibleTenants = accessibleTenants;
    }),
  );

  getCurrentTenantLogo$ = this.commonService.getCurrentTenantLogo$();
  getAccountMenuData$ = this.userDataFacade.userData$.pipe(
    filter(Boolean),
    switchMap(() =>
      this.commonService.getAccountMenuData$().pipe(
        catchError((err: unknown) => {
          this.logger.error('Failed to load account menu data', err);
          return of(null);
        }),
      ),
    ),
    tap((data) => this.logger.log('Account Menu Data: ', data)),
  );

  getUserNotifications$ = this.commonService
    .getUserNotifications(this.customActionButtonsConfig())()
    .pipe(map((data) => data.records));
  getNotifications$ = this.commonService.getNotifications$().pipe(
    withLatestFrom(this.commonService.getMarkingAllAsRead$()),
    switchMap(([userNotifications, markingAllAsRead]) => {
      if (markingAllAsRead) {
        const updatedNotifications = userNotifications.map((notification) => {
          if (!notification.isRead) {
            this.commonService.setNotificationSpinner(notification.id, true);
            return { ...notification, isRead: true };
          }
          return notification;
        });
        return of(updatedNotifications);
      } else {
        return of(userNotifications);
      }
    }),
  );
  refreshNotificationsGrid$ = this.commonService.refreshNotificationsGrid$().pipe(
    switchMap(() => this.commonService.getUserNotificationCounts$()),
    switchMap(() => this.commonService.getUserNotifications(this.customActionButtonsConfig())()),
  );
  getUnreadNotificationsCount$ = this.commonService.getUnreadNotificationsCount$();
  getUserNotificationCounts$ = this.commonService.getUserNotificationCounts$();
  getTotalNotificationsCount$ = this.commonService.getTotalNotificationsCount$();
  notificationsInProgress$ = this.commonService.getNotificationsInProgress$();
  refreshNotifications$ = this.commonService.refreshNotificationsGrid$();
  getMarkingAllAsRead$ = this.commonService.getMarkingAllAsRead$();

  onEulaAccepted$ = this.controlPageFacade.isEulaAccepted$;

  signalRNotifyProgress$ = this.signalRService.notifyProgress$.pipe(
    withLatestFrom(this.commonService.getUnreadNotificationsCount$()),
    tap(([notifyProgress, unreadNotificationsCount]) => {
      if (notifyProgress?.isComplete) {
        if (unreadNotificationsCount <= 100) {
          this.commonService.setMarkingAllAsRead(false);
          this.commonService.refreshNotificationsGrid();
        } else {
          const timeout = unreadNotificationsCount * 3;
          setTimeout(() => {
            this.commonService.setMarkingAllAsRead(false);
            this.commonService.refreshNotificationsGrid();
          }, timeout);
        }
      } else {
        this.commonService.setMarkingAllAsRead(false);
      }
    }),
  );

  private skippedNotifications = 0;

  signalRUserNotifications$ = this.signalRService.userNotification$.pipe(
    withLatestFrom(this.commonService.getMarkingAllAsRead$()),
    mergeMap(([notificationOnSignalR, markingAllAsRead]) =>
      iif(
        () => Boolean(markingAllAsRead),
        of(notificationOnSignalR).pipe(
          tap((notification) => {
            this.skippedNotifications = this.skippedNotifications + 1;
            this.commonService.setNotificationSpinner(notification.id, false);
            if (this.skippedNotifications % 100 === 0) {
              this.commonService.refreshNotificationsGrid();
            }
          }),
        ),
        of(notificationOnSignalR).pipe(
          withLatestFrom(this.commonService.getNotifications$(), this.commonService.getUnreadNotificationsCount$()),
          tap(([notificationOnSignalR, notifications, unreadNotificationCount]) => {
            let updatedNotifications: UserNotification[];
            let count = unreadNotificationCount;

            this.skippedNotifications = 0;

            const existingNotification = notifications.find(
              (notification) => notification.id === notificationOnSignalR.id,
            );

            if (existingNotification) {
              updatedNotifications = notifications.map((notification) =>
                notification.id === notificationOnSignalR.id
                  ? { ...notification, isRead: notificationOnSignalR.isRead }
                  : notification,
              );
            } else {
              updatedNotifications = [notificationOnSignalR, ...notifications].slice(0, 10);
            }

            count = notificationOnSignalR.isRead ? Math.max(0, count - 1) : count + 1;

            this.commonService.setNotification(updatedNotifications);
            this.commonService.refreshNotificationsGrid();
            this.commonService.setUnreadNotificationsCount(count);
          }),
        ),
      ),
    ),
  );

  notificationService$ = this.commonService
    .getUserNotifications(this.customActionButtonsConfig())
    .bind(this.commonService);

  homeMenuTitle: string;

  notificationAction(data: any): void {
    this.notificationAction$ = this.commonService.handleNotificationAction$(data);
  }

  get logoTypeThemeClass(): string {
    return this.navbarTheme === NavBarTheme.DARK ? 'ra-logo-ft-hub-dark' : 'ra-logo-ft-hub-light';
  }

  setLogoTheme(theme: NavBarTheme): void {
    this.navbarTheme = theme;
  }

  setSelectedLang(selectedLangItem: LanguageItem): void {
    this.selectedLang = selectedLangItem;
    this.userDataFacade.languageChange(this.selectedLang.langCode);
    this.translate.use(this.selectedLang.langCode);
  }

  joinOrgClicked(_$event): void {
    this.controlPageFacade.navigateTo('/organizations/join');
  }

  manageOrganizationsAction(): void {
    this.commonService.openManageOrganizations();
  }

  onRaLogoClick(): void {
    this.userDataFacade.currentTenantContext$
      .pipe(
        mergeMap((currentTenantContext) =>
          iif(
            () => currentTenantContext === 'NotSelected' || currentTenantContext === 'Personal',
            of({ navigateTo: TENANT_MGMT_ROUTES.ORGANIZATIONS }),
            this.userDataFacade.hasAnyServiceRole$.pipe(
              switchMap((tenantAccess) => {
                // Explicit type assertion to ensure TypeScript recognizes the narrowed type
                const tenantContext = currentTenantContext as SelectedTenant;
                const redirectPath = tenantAccess === 'Service' ? this.DASHBOARD_PATH : this.SUB_ORGS_PATH;
                return of({ navigateTo: `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${tenantContext.id}${redirectPath}` });
              }),
            ),
          ),
        ),
        take(1),
        tap((context) => {
          this.router.navigate([context.navigateTo]);
        }),
      )
      .subscribe();
  }

  customActionButtonsConfig(): (notification: UserNotification) => CustomAction[] {
    return this.accessRequestPredicate();
  }

  accessRequestPredicate(): CustomActionPredicate {
    return (notification: UserNotification): CustomAction[] => {
      if (notification.customJson2?.eventType === AccessRequestEvent.AccessRequestCreated) {
        return [
          {
            label: 'Go to Approve users',
            id: 'go-to-approve-users',
            icon: 'ra-icon-ide-md-right-arrow',
            data: {
              clickToClosePanel: true,
            },
          },
        ];
      }
      return [];
    };
  }

  notificationCustomActionClicked($event): void {
    if ($event.customAction.id === 'go-to-approve-users') {
      const previousTenant = R.clone(this.selectedTenant);
      if ($event.notification.customJson2?.data?.tenantId === previousTenant.id) {
        this.router.navigate([
          `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${$event.notification.customJson2.data.tenantId}/approve-users`,
        ]);
      } else {
        this.userDataFacade.tenantChange(
          $event.notification.customJson2?.data?.tenantId,
          `${TENANT_MGMT_ROUTES.ORGANIZATIONS}/${$event.notification.customJson2.data.tenantId}/approve-users`,
        );
        const targetTenant = this.accessibleTenants.find(
          (el) => el.id === $event.notification.customJson2?.data?.tenantId,
        );
        this.uiStateFacade.displayMessage({
          message: `You are now in ${targetTenant?.name} organization.`,
          type: 'Info',
        });
      }
    }
  }
}
